import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, ArrowRight } from "lucide-react";

const STAGES = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "qualified", label: "Qualified" },
  { key: "proposal", label: "Proposal" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];

const SOURCES = ["referral", "website", "cold_call", "linkedin", "event", "other"];

const INR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

function LeadCard({ lead, onStatusChange, onConvert }) {
  const nextStages = useMemo(
    () => STAGES.filter((s) => s.key !== lead.status).slice(0, 3),
    [lead.status],
  );
  return (
    <div className="border border-zinc-200 rounded-md bg-white p-3 hover:border-zinc-900 transition-colors" data-testid={`lead-card-${lead.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{lead.name}</div>
          <div className="text-[11px] text-zinc-500 truncate">{lead.company || "—"}</div>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 shrink-0">{lead.source}</span>
      </div>
      <div className="mt-3 font-mono text-[11px] text-zinc-700">{INR(lead.est_value)}</div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {nextStages.map((s) => (
          <button
            key={s.key}
            onClick={() => onStatusChange(lead.id, s.key)}
            className="text-[10px] uppercase tracking-widest border border-zinc-200 hover:border-zinc-900 rounded px-1.5 py-0.5 text-zinc-600 transition-colors"
            data-testid={`lead-move-${lead.id}-${s.key}`}
          >
            → {s.label}
          </button>
        ))}
        {lead.status !== "won" && !lead.converted_client_id && (
          <button
            onClick={() => onConvert(lead.id)}
            className="text-[10px] uppercase tracking-widest bg-zinc-900 text-white rounded px-1.5 py-0.5 hover:bg-zinc-700 transition-colors flex items-center gap-1"
            data-testid={`lead-convert-${lead.id}`}
          >
            Convert <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function Leads() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("kanban");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", company: "", contact_email: "", contact_phone: "", source: "website", est_value: 0, notes: "" });

  const { data: leads = [] } = useQuery({ queryKey: ["leads"], queryFn: async () => (await api.get("/leads")).data });

  const statusMut = useMutation({
    mutationFn: ({ id, status }) => api.patch(`/leads/${id}`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leads"] }); qc.invalidateQueries({ queryKey: ["pipeline"] }); },
  });
  const convertMut = useMutation({
    mutationFn: (id) => api.post(`/leads/${id}/convert`),
    onSuccess: () => {
      toast.success("Lead converted to a Client");
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["kpis"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const createMut = useMutation({
    mutationFn: (payload) => api.post("/leads", payload),
    onSuccess: () => {
      toast.success("Lead added to pipeline");
      setOpen(false);
      setForm({ name: "", company: "", contact_email: "", contact_phone: "", source: "website", est_value: 0, notes: "" });
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["pipeline"] });
      qc.invalidateQueries({ queryKey: ["kpis"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const submit = (e) => {
    e.preventDefault();
    createMut.mutate({ ...form, est_value: Number(form.est_value) || 0, contact_email: form.contact_email || undefined });
  };

  const grouped = STAGES.reduce((acc, s) => {
    acc[s.key] = leads.filter((l) => l.status === s.key);
    return acc;
  }, {});

  return (
    <div data-testid="leads-page">
      <PageHeader
        eyebrow="Business Division · CRM"
        title="Leads"
        description="Every relationship starts here. Move leads through the pipeline and convert to clients."
        actions={
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button data-testid="new-lead-btn"><Plus className="w-4 h-4 mr-1.5" /> New Lead</Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md">
              <SheetHeader><SheetTitle>Add a new lead</SheetTitle></SheetHeader>
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="new-lead-form">
                <div><Label htmlFor="l-name">Contact name</Label><Input id="l-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="lead-name" /></div>
                <div><Label htmlFor="l-company">Company</Label><Input id="l-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="lead-company" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label htmlFor="l-email">Email</Label><Input id="l-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="lead-email" /></div>
                  <div><Label htmlFor="l-phone">Phone</Label><Input id="l-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="lead-phone" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Source</Label>
                    <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                      <SelectTrigger data-testid="lead-source"><SelectValue /></SelectTrigger>
                      <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label htmlFor="l-value">Est. Value (INR)</Label><Input id="l-value" type="number" value={form.est_value} onChange={(e) => setForm({ ...form, est_value: e.target.value })} data-testid="lead-value" /></div>
                </div>
                <div><Label htmlFor="l-notes">Notes</Label><Textarea id="l-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="lead-notes" /></div>
                <SheetFooter><Button type="submit" disabled={createMut.isPending} data-testid="lead-submit">Add Lead</Button></SheetFooter>
              </form>
            </SheetContent>
          </Sheet>
        }
      />
      <div className="max-w-[1400px] mx-auto px-8 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="leads-tabs">
            <TabsTrigger value="kanban" data-testid="leads-tab-kanban">Pipeline</TabsTrigger>
            <TabsTrigger value="list" data-testid="leads-tab-list">List</TabsTrigger>
          </TabsList>
          <TabsContent value="kanban" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="leads-kanban">
              {STAGES.map((s) => (
                <div key={s.key} className="border border-zinc-200 rounded-lg bg-zinc-50 p-3 min-h-[200px]" data-testid={`kanban-col-${s.key}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{s.label}</div>
                    <Badge variant="secondary" className="text-[10px]">{grouped[s.key].length}</Badge>
                  </div>
                  <div className="space-y-2">
                    {grouped[s.key].map((l) => (
                      <LeadCard key={l.id} lead={l} onStatusChange={(id, status) => statusMut.mutate({ id, status })} onConvert={(id) => convertMut.mutate(id)} />
                    ))}
                    {grouped[s.key].length === 0 && <div className="text-[11px] text-zinc-400 font-mono">— empty —</div>}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="list" className="mt-6">
            <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden" data-testid="leads-list">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="px-4 py-3">Contact</th><th className="px-4 py-3">Company</th>
                    <th className="px-4 py-3">Source</th><th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {leads.map((l) => (
                    <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                      <td className="px-4 py-3 font-medium">{l.name}</td>
                      <td className="px-4 py-3 text-zinc-600">{l.company || "—"}</td>
                      <td className="px-4 py-3 text-zinc-600 capitalize">{l.source.replace("_", " ")}</td>
                      <td className="px-4 py-3"><Badge variant="outline" className="capitalize">{l.status}</Badge></td>
                      <td className="px-4 py-3 text-right font-mono">{INR(l.est_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
